import React from 'react';

// import dayjs from 'dayjs';
import cn from 'classnames';
import PropTypes from 'prop-types';

// import { toastWarning } from '../toast';
import FormInputSelect from './FormInputSelect';

const icon = (
  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
      clipRule="evenodd"
    />
  </svg>
);

const TIME_OPTS = [
  {
    label: '07:00 AM',
    value: '07:00 AM',
  },
  {
    label: '08:00 AM',
    value: '08:00 AM',
  },
  {
    label: '09:00 AM',
    value: '09:00 AM',
  },
  {
    label: '10:00 AM',
    value: '10:00 AM',
  },
  {
    label: '11:00 AM',
    value: '11:00 AM',
  },
  {
    label: '12:00 PM',
    value: '12:00 PM',
  },
  {
    label: '01:00 PM',
    value: '01:00 PM',
  },
  {
    label: '02:00 PM',
    value: '02:00 PM',
  },
  {
    label: '03:00 PM',
    value: '03:00 PM',
  },
  {
    label: '04:00 PM',
    value: '04:00 PM',
  },
  {
    label: '05:00 PM',
    value: '05:00 PM',
  },
  {
    label: '06:00 PM',
    value: '06:00 PM',
  },
  {
    label: '07:00 PM',
    value: '07:00 PM',
  },
  {
    label: '08:00 PM',
    value: '08:00 PM',
  },
  {
    label: '09:00 PM',
    value: '09:00 PM',
  },
  {
    label: '10:00 PM',
    value: '10:00 PM',
  },
  {
    label: '11:00 PM',
    value: '11:00 PM',
  },
  {
    label: '12:00 AM',
    value: '12:00 AM',
  },
  {
    label: '01:00 AM',
    value: '01:00 AM',
  },
  {
    label: '02:00 AM',
    value: '02:00 AM',
  },
  {
    label: '03:00 AM',
    value: '03:00 AM',
  },
  {
    label: '04:00 AM',
    value: '04:00 AM',
  },
  {
    label: '05:00 AM',
    value: '05:00 AM',
  },
  {
    label: '06:00 AM',
    value: '06:00 AM',
  },
];

const defaultValue = (v, type, isMultiple, key) => {
  let x = '';
  if (v && !type && isMultiple) {
    // Is multiple and not differential
    try {
      if (Array.isArray(v) && !type) {
        x = {
          start: v
            ? v?.map((i) => ({
                value: i,
                label: i,
              }))
            : v,
          end: '',
        };
        return x;
      }
      throw new Error(
        `Hoy!!!, Component with name ${key} is using value with STRING, it should be in ARRAY format!`
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err, ' here');
    }
  }

  // Is not multiple and not differential
  if (!type && !isMultiple) {
    try {
      if (typeof v === 'string' && !type) {
        x = {
          start: v
            ? {
                value: v,
                label: v,
              }
            : '',
          end: '',
        };
        return x;
      }
      throw new Error(
        `Hoy!!!, Component with name ${key} is using value with OBJECT, it should be in STRING format!`
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err, ' here');
    }
  }
  // Is not multiple and differential
  if (type && !isMultiple) {
    try {
      if (typeof v === 'object' && type) {
        x = {
          start: v?.start ?? '',
          end: v?.end ?? '',
        };
        return x;
      }
      throw new Error(
        `Component with name ${key} is using value with STRING, it should be in OBJECT format!`
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err, ' here');
    }
  }
  return x;
};

const FormInputTime = React.forwardRef((props, ref) => {
  const {
    id,
    name,
    value,
    label,
    error,
    onChange,
    required,
    className,
    placeholder,
    sizeClassName,
    isDifferential,
    labelClassName,
    containerClassName,
    isMultiple,
    onSetFieldValue,
    smallTime,
    ...rest
  } = props;

  const $TIME_OPTS = smallTime
    ? TIME_OPTS?.map((item) => ({
        label: (item?.label || '')?.toLowerCase(),
        value: (item?.value || '')?.toLowerCase(),
      }))
    : TIME_OPTS;

  const clickRef = React.useRef(null);
  const [isShow, setIfShow] = React.useState(false);
  const [animate, setAnimate] = React.useState(false);

  const [time, setTime] = React.useState(
    defaultValue(value, isDifferential, isMultiple, name)
  );

  const [timeTemp, setTimeTemp] = React.useState(
    defaultValue(value, isDifferential, isMultiple, name)
  );

  const handleOnSetActive = (e) => {
    e.preventDefault();
    if (!isShow) {
      setIfShow(true);
      setTimeout(() => {
        setAnimate(true);
      }, 100);
      return;
    }
    setAnimate(false);
    setTimeout(() => {
      setIfShow(false);
    }, 100);
  };

  const handleOnClose = (e) => {
    if (e) e.preventDefault();
    setAnimate(false);
    setTimeout(() => {
      setIfShow(false);
    }, 100);
  };

  const handleOnSubmitTimeTemp = (e) => {
    e.preventDefault();
    handleOnClose();
    const args = {
      ...time,
      ...timeTemp,
    };
    setTime(args);
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, args);
      return;
    }
    if (typeof onChange === 'function')
      onChange((prev) => ({ ...prev, [name]: args }));
  };

  const handleOnChangeSelectSingleTime = (v) => {
    const $v = v();

    setTime((prev) => ({
      ...prev,
      ...$v,
    }));

    if (isMultiple) {
      if (typeof onChange === 'function') {
        onChange((prev) => ({
          ...prev,
          [name]: $v?.start?.map((x) => x?.value),
        }));
        return;
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(
          name,
          $v?.start?.map((x) => x?.value)
        );
      }
      return;
    }

    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, $v?.start?.value);
      return;
    }

    if (typeof onChange === 'function') {
      onChange((prev) => ({ ...prev, [name]: $v?.start?.value }));
    }
  };

  const handleOnChangeSelectTemp = (v) => {
    const $v = v();
    setTimeTemp((prev) => ({
      ...prev,
      ...$v,
    }));
  };

  // React.useEffect(() => {
  //   const st = dayjs(`2000-01-01 ${timeTemp?.start?.value}`);
  //   const et = dayjs(`2000-01-01 ${timeTemp?.end?.value}`);
  //   if (st?.isValid() && et?.isValid()) {
  //     toastWarning('Start time must be lowerthan to end time');
  //     setTimeTemp((prev) => ({
  //       ...prev,
  //       end: '',
  //     }));
  //   }
  // }, [timeTemp?.end?.value, timeTemp?.start?.value]);

  React.useEffect(() => {
    const escFunction = (e) => {
      if (e.keyCode === 27) {
        setAnimate(false);
        setTimeout(() => {
          setIfShow(false);
        }, 100);
      }
    };

    const handleClearToggle = (e) => {
      try {
        if (clickRef.current && !clickRef.current.contains(e.target)) {
          setAnimate(false);
          setTimeout(() => {
            setIfShow(false);
          }, 100);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err, ' here');
      }
    };

    if (isShow) {
      document.addEventListener('keydown', escFunction, false);
      document.addEventListener('mousedown', handleClearToggle, false);
    }
    return () => {
      document.removeEventListener('keydown', escFunction, false);
      document.removeEventListener('mousedown', handleClearToggle, false);
    };
  }, [id, isShow, ref]);

  React.useEffect(() => {
    if (value !== '') {
      setTime(defaultValue(value, isDifferential, isMultiple, name));
      setTimeTemp(defaultValue(value, isDifferential, isMultiple, name));
    }
  }, [isDifferential, isMultiple, name, value]);

  return (
    <>
      {!isDifferential ? (
        <FormInputSelect
          {...rest}
          isMultiple={isMultiple}
          value={time?.start ?? ''}
          isSorted={false}
          name="start"
          icon={icon}
          label={label}
          required={required}
          options={$TIME_OPTS}
          onChange={handleOnChangeSelectSingleTime}
          placeholder={placeholder}
          error={error}
        />
      ) : (
        <div className="w-full relative" ref={clickRef}>
          <div
            className={cn(
              'form-container transition duration-300 ease-in-out',
              containerClassName,
              {
                'form-container': !error,
                'form-container-error': error,
              }
            )}
          >
            {label && (
              <label
                className={cn('form-label', labelClassName)}
                htmlFor={id ?? name}
              >
                {label}{' '}
                {required ? <span className="text-red-500">*</span> : ''}
              </label>
            )}
            <div className="relative w-full">
              <div
                className={cn(
                  'transition duration-300 ease-in-out cursor-pointer',
                  {
                    'form-input': !error,
                    'form-input-error': error,
                  },
                  {
                    'border-form-active': isShow,
                  },
                  className
                )}
                role="presentation"
                onClick={handleOnSetActive}
              >
                <div className="flex space-x-2">
                  {icon}
                  {time?.start?.value !== time?.end?.value ? (
                    <span>
                      {time?.start && time?.end
                        ? `${time?.start?.value} - ${time?.end?.value}`
                        : `${placeholder}`}
                    </span>
                  ) : (
                    <span>
                      {time?.start && time?.end ? '24 Hours' : `${placeholder}`}
                    </span>
                  )}
                </div>
              </div>
              {error && (
                <small className="form-error-badge">{error ?? ''}</small>
              )}
            </div>
          </div>
          {isShow && (
            <div
              id={id}
              className={cn(
                'transition transform ease-out duration-100 origin-top z-50 absolute right-0 mt-1 to flex flex-col bg-white border border-gray-100 rounded-md shadow h-auto max-h-80',
                {
                  'opacity-0 scale-95': !animate,
                  'opacity-100 scale-100': animate,
                },
                {
                  [sizeClassName]: sizeClassName,
                }
              )}
            >
              <div className="p-5">
                <div className="space-y-5">
                  <div className="grid grid-cols-2 items-center gap-3">
                    <div className="col-span-2 lg:col-span-1">
                      <FormInputSelect
                        isSorted={false}
                        label="Start Time"
                        placeholder="Select time..."
                        name="start"
                        onChange={handleOnChangeSelectTemp}
                        options={$TIME_OPTS}
                        value={timeTemp?.start ?? ''}
                      />
                    </div>
                    <div className="col-span-2 lg:col-span-1">
                      <FormInputSelect
                        isSorted={false}
                        label="End Time"
                        placeholder="Select time..."
                        name="end"
                        onChange={handleOnChangeSelectTemp}
                        options={$TIME_OPTS}
                        value={timeTemp?.end ?? ''}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      type="button"
                      className="rounded-md border p-2 px-4 w-32"
                      onClick={handleOnClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      onClick={handleOnSubmitTimeTemp}
                      className="rounded-md p-2 px-4 border bg-primary-500 text-white w-32"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
});

// If type of input is isDifferential = true make your value is object with start and end
// ex:
//   value:{{
//     start: '10:00 AM',
//     end: '10:00 AM'
// }}
// If your type of input is isDifferential = false make sure your value is string
// ex:
// value: '10:00 AM'

FormInputTime.defaultProps = {
  error: false,
  id: 'select-time',
  isDifferential: false,
  placeholder: 'Select Time',
  label: 'Name',
  containerClassName: '',
  isMultiple: false,
  sizeClassName: 'w-full',
  labelClassName: '',
  className: '',
  required: false,
  value: '',
  onChange: false,
  onSetFieldValue: false,
  smallTime: false,
};

FormInputTime.propTypes = {
  id: PropTypes.string,
  required: PropTypes.bool,
  isMultiple: PropTypes.bool,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  sizeClassName: PropTypes.string,
  isDifferential: PropTypes.bool,
  onChange: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  label: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
  ]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Object)]),
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  className: PropTypes.string,
  smallTime: PropTypes.bool,
};

export default FormInputTime;
